// @flow
import React, { Fragment, useState } from 'react'
import UrlInput from '../AdEdit/AdInputs/UrlInput'
import styled, { type StyledComponent } from 'styled-components'
import TextInput from '../AdEdit/AdInputs/TextInput'
import type { Configs, OptionType } from '../types'
import SelectInput from './AdInputs/SelectInput'
import Checkbox from '../controls/Checkbox'
import _ from 'lodash'

const Row: StyledComponent<Object, any, any> = styled.div`
  padding-bottom: 14px;
  white-space: nowrap;
  max-width: 675px;
`

const t = {
  url: 'Ссылка',
  vk_promotion_url: 'Ссылка на пост в ВКонтакте',
  tm_promotion_url: 'Ссылка на пост в Одноклассниках',
  fb_promotion_url: 'Ссылка на пост в Facebook',
  title: 'Заголовок',
  vk_extended_title: 'Расширенный заголовок для ВКонтакте',
  fb_extended_title: 'Расширенный заголовок для Facebook',
  product_title: 'Заголовок для товара',
  description: 'Описание',
  extended_description_note: 'Необязательное. По умолчанию совпадает с общим описанием',
  column_description: 'Описание для колонки',
  multi_description: 'Описание для мультиформата',
  product_description: 'Описание для товара',
  vk_multi_description: 'Описание для ВКонтакте',
  vk_article_description: 'Описание для ВКонтакте без кнопки',
  fb_multi_description: 'Описание для Facebook',
  vk_link_title: 'Текст рядом с логотипом в ВКонтакте',
  choose_fb_post: 'Выберите ссылку на пост в Facebook',
  custom_fields: 'Более тонкая настройка заголовка и описания',
  cut_view_unavailable: '(сокращённая форма недоступна)',
  tm_carousel_description: 'Описание для карусели myTarget'
}

type Props = {|
  allowCutView: boolean,
  columnDescription: string,
  columnDescriptionErrors: Array<string>,
  configs: Configs,
  fbMultiDescription: string,
  fbMultiDescriptionErrors: Array<string>,
  fbPromotionPosts: Array<OptionType>,
  fbTitle: string,
  fbTitleErrors: Array<string>,
  fbUrl: string,
  fbUrlErrors: Array<string>,
  isFbUrlDisabled: boolean,
  isTmUrlDisabled: boolean,
  isVkUrlDisabled: boolean,
  multiDescription: string,
  multiDescriptionErrors: Array<string>,
  onColumnDescriptionChange: (string) => void,
  onFbMultiDescriptionChange: (string) => void,
  onFbTitleChange: (string) => void,
  onFbUrlChange: (string) => void,
  onMultiDescriptionChange: (string) => void,
  onProductDescriptionChange: (string) => void,
  onProductTitleChange: (string) => void,
  onTitleChange: (string) => void,
  onTmCarouselDescriptionChange: (string) => void,
  onTmUrlChange: (string) => void,
  onUrlChange: (string) => void,
  onVkLinkTitleChange: (string) => void,
  onVkMultiDescriptionChange: (string) => void,
  onVkTitleChange: (string) => void,
  onVkUrlChange: (string) => void,
  onVkWithoutButtonDescriptionChange: (string) => void,
  productDescription: string,
  productDescriptionErrors: Array<string>,
  productTitle: string,
  productTitleErrors: Array<string>,
  showColumnDescription: boolean,
  showFbMultiDescription: boolean,
  showFbTitle: boolean,
  showFbUrl: boolean,
  showMultiDescription: boolean,
  showProductDescription: boolean,
  showProductTitle: boolean,
  showTitle: boolean,
  showTmCarouselDescription: boolean,
  showTmUrl: boolean,
  showUrl: boolean,
  showVkLinkTitle: boolean,
  showVkMultiDescription: boolean,
  showVkTitle: boolean,
  showVkUrl: boolean,
  showVkWithoutButtonDescription: boolean,
  title: string,
  titleErrors: Array<string>,
  tmCarouselDescription: string,
  tmCarouselDescriptionErrors: Array<string>,
  tmUrl: string,
  tmUrlErrors: Array<string>,
  url: string,
  urlCompact: boolean,
  urlErrors: Array<string>,
  vkLinkTitle: string,
  vkLinkTitleErrors: Array<string>,
  vkMultiDescription: string,
  vkMultiDescriptionErrors: Array<string>,
  vkTitle: string,
  vkTitleErrors: Array<string>,
  vkUrl: string,
  vkUrlErrors: Array<string>,
  vkWithoutButtonDescription: string,
  vkWithoutButtonDescriptionErrors: Array<string>
|}

export default function AdInputs(props: Props) {
  const configs = props.configs
  const cutViewData = calculateCutViewData(props)

  const [fullView, setFullView] = useState(!props.allowCutView || !cutViewData.isCutViewAvailable)

  return (
    <Fragment>
      {props.showUrl && (
        <Row className="t-Url">
          <UrlInput
            value={props.url}
            errors={props.urlErrors}
            header={t.url}
            compact={props.urlCompact}
            onChange={props.onUrlChange}
          />
        </Row>
      )}
      {props.showVkUrl && (
        <Row className="t-VkUrl">
          <UrlInput
            value={props.vkUrl}
            errors={props.vkUrlErrors}
            header={t.vk_promotion_url}
            compact
            onChange={props.onVkUrlChange}
            disabled={props.isVkUrlDisabled}
          />
        </Row>
      )}
      {props.showTmUrl && (
        <Row className="t-TmUrl">
          <UrlInput
            value={props.tmUrl}
            errors={props.tmUrlErrors}
            header={t.tm_promotion_url}
            compact
            onChange={props.onTmUrlChange}
            disabled={props.isTmUrlDisabled}
          />
        </Row>
      )}
      {props.showFbUrl && (
        <Row className="t-FbUrl">
          <SelectInput
            className="t-FbPromotionPostSelect"
            value={props.fbUrl}
            errors={props.fbUrlErrors}
            header={t.fb_promotion_url}
            options={[{ id: '', name: t.choose_fb_post }, ...props.fbPromotionPosts]}
            onChange={props.onFbUrlChange}
            fullWidth
            disabled={props.isFbUrlDisabled}
          />
        </Row>
      )}
      {!fullView && cutViewData.showTitle && (
        <Row>
          <TextInput header={t.title} className="t-CommonTitle" {...cutViewData.titleProps} />
        </Row>
      )}
      {props.showTitle && fullView && (
        <Row>
          <TextInput
            header={t.title}
            maxLength={configs.titleMaxLength}
            value={props.title}
            errors={props.titleErrors}
            className="t-Title"
            onChange={props.onTitleChange}
          />
        </Row>
      )}
      {props.showProductTitle && fullView && (
        <Row>
          <TextInput
            header={t.product_title}
            maxLength={configs.extendedTitleMaxLength}
            value={props.productTitle}
            errors={props.productTitleErrors}
            className="t-ProductTitle"
            onChange={props.onProductTitleChange}
          />
        </Row>
      )}
      {props.showVkTitle && fullView && (
        <Row>
          <TextInput
            header={t.vk_extended_title}
            maxLength={configs.extendedTitleMaxLength}
            value={props.vkTitle}
            errors={props.vkTitleErrors}
            className="t-VkTitle"
            onChange={props.onVkTitleChange}
          />
        </Row>
      )}
      {props.showFbTitle && fullView && (
        <Row>
          <TextInput
            header={t.fb_extended_title}
            maxLength={configs.extendedTitleMaxLength}
            value={props.fbTitle}
            errors={props.fbTitleErrors}
            className="t-FbTitle"
            onChange={props.onFbTitleChange}
          />
        </Row>
      )}
      {!fullView && cutViewData.showDescription && (
        <Row>
          <TextInput
            header={t.description}
            className="t-CommonDescription"
            {...cutViewData.descriptionProps}
          />
        </Row>
      )}
      {props.showColumnDescription && fullView && (
        <Row>
          <TextInput
            header={t.column_description}
            maxLength={configs.descriptionMaxLength}
            value={props.columnDescription}
            errors={props.columnDescriptionErrors}
            className="t-ColumnDescription"
            onChange={props.onColumnDescriptionChange}
          />
        </Row>
      )}
      {props.showMultiDescription && fullView && (
        <Row>
          <TextInput
            header={t.multi_description}
            maxLength={configs.multiDescriptionMaxLength}
            value={props.multiDescription}
            errors={props.multiDescriptionErrors}
            className="t-MultiDescription"
            onChange={props.onMultiDescriptionChange}
          />
        </Row>
      )}
      {props.showProductDescription && fullView && (
        <Row>
          <TextInput
            header={t.product_description}
            maxLength={configs.descriptionMaxLength}
            value={props.productDescription}
            errors={props.productDescriptionErrors}
            className="t-ProductDescription"
            onChange={props.onProductDescriptionChange}
          />
        </Row>
      )}
      {props.showVkMultiDescription && fullView && (
        <Row>
          <TextInput
            header={t.vk_multi_description}
            maxLength={configs.vkExtendedDescriptionMaxLength}
            value={props.vkMultiDescription}
            errors={props.vkMultiDescriptionErrors}
            className="t-VkMultiDescription"
            multiline
            onChange={props.onVkMultiDescriptionChange}
          />
        </Row>
      )}
      {props.showVkWithoutButtonDescription && fullView && (
        <Row>
          <TextInput
            header={t.vk_article_description}
            maxLength={configs.vkWithoutButtonDescriptionMaxLength}
            value={props.vkWithoutButtonDescription}
            errors={props.vkWithoutButtonDescriptionErrors}
            className="t-VkWithoutButtonDescription"
            multiline
            onChange={props.onVkWithoutButtonDescriptionChange}
          />
        </Row>
      )}
      {props.showFbMultiDescription && fullView && (
        <Row>
          <TextInput
            header={t.fb_multi_description}
            maxLength={configs.fbExtendedDescriptionMaxLength}
            value={props.fbMultiDescription}
            errors={props.fbMultiDescriptionErrors}
            className="t-FbMultiDescription"
            multiline
            onChange={props.onFbMultiDescriptionChange}
          />
        </Row>
      )}
      {props.showTmCarouselDescription && fullView && (
        <Row>
          <TextInput
            header={t.tm_carousel_description}
            maxLength={configs.tmCarouselDescriptionMaxLength}
            value={props.tmCarouselDescription}
            errors={props.tmCarouselDescriptionErrors}
            className="t-TmCarouselDescription"
            multiline
            onChange={props.onTmCarouselDescriptionChange}
          />
        </Row>
      )}
      {props.showVkLinkTitle && (
        <Row>
          <TextInput
            header={t.vk_link_title}
            maxLength={configs.vkLinkTitleMaxLength}
            value={props.vkLinkTitle}
            errors={props.vkLinkTitleErrors}
            className="t-VkLinkTitle"
            onChange={props.onVkLinkTitleChange}
          />
        </Row>
      )}
      {props.allowCutView && cutViewData.isCutViewReasonable && (
        <Checkbox
          className="t-FullViewCheckbox"
          checked={fullView}
          disabled={!cutViewData.isCutViewAvailable}
          onChange={(checked) => setFullView(checked)}
          label={
            t.custom_fields + (cutViewData.isCutViewAvailable ? '' : ' ' + t.cut_view_unavailable)
          }
        />
      )}
    </Fragment>
  )
}

function calculateCutViewData(
  props: Props
): {|
  titleProps: {|
    maxLength: number,
    errors: Array<string>,
    value: string,
    onChange: (string) => void
  |},
  showTitle: boolean,
  descriptionProps: {|
    maxLength: number,
    errors: Array<string>,
    value: string,
    multiline: boolean,
    onChange: (string) => void
  |},
  showDescription: boolean,
  isCutViewAvailable: boolean,
  isCutViewReasonable: boolean
|} {
  const uniqTitles = _.uniq(
    _.filter([
      props.showTitle && props.title,
      props.showVkTitle && props.vkTitle,
      props.showFbTitle && props.fbTitle,
      props.showProductTitle && props.productTitle
    ])
  )
  const uniqDescriptions = _.uniq(
    _.filter([
      props.showColumnDescription && props.columnDescription,
      props.showMultiDescription && props.multiDescription,
      props.showProductDescription && props.productDescription,
      props.showVkMultiDescription && props.vkMultiDescription,
      props.showVkWithoutButtonDescription && props.vkWithoutButtonDescription,
      props.showFbMultiDescription && props.fbMultiDescription,
      props.showTmCarouselDescription && props.tmCarouselDescription
    ])
  )

  const resultDescription = uniqDescriptions[0] || ''
  const resultTitle = uniqTitles[0] || ''

  const titleMaxLength =
    _.min(
      _.filter([
        props.showTitle && props.configs.titleMaxLength,
        props.showProductTitle && props.configs.extendedTitleMaxLength,
        props.showVkTitle && props.configs.extendedTitleMaxLength,
        props.showFbTitle && props.configs.extendedTitleMaxLength
      ])
    ) || 0
  const descriptionMaxLength =
    _.min(
      _.filter([
        props.showColumnDescription && props.configs.descriptionMaxLength,
        props.showMultiDescription && props.configs.multiDescriptionMaxLength,
        props.showProductDescription && props.configs.descriptionMaxLength,
        props.showVkMultiDescription && props.configs.vkExtendedDescriptionMaxLength,
        props.showVkWithoutButtonDescription && props.configs.vkWithoutButtonDescriptionMaxLength,
        props.showFbMultiDescription && props.configs.fbExtendedDescriptionMaxLength,
        props.showTmCarouselDescription && props.configs.tmCarouselDescriptionMaxLength
      ])
    ) || 0

  const showTitle =
    props.showTitle || props.showProductTitle || props.showVkTitle || props.showFbTitle

  const titleProps = {
    maxLength: titleMaxLength,
    value: resultTitle,
    errors: _.uniq([
      ...(props.showTitle ? props.titleErrors : []),
      ...(props.showProductTitle ? props.productTitleErrors : []),
      ...(props.showVkTitle ? props.vkTitleErrors : []),
      ...(props.showFbTitle ? props.fbTitleErrors : [])
    ]),
    onChange: (val) => {
      if (props.showTitle) props.onTitleChange(val)
      if (props.showProductTitle) props.onProductTitleChange(val)
      if (props.showVkTitle) props.onVkTitleChange(val)
      if (props.showFbTitle) props.onFbTitleChange(val)
    }
  }

  const isCutViewReasonable =
    _.filter([
      props.showColumnDescription,
      props.showMultiDescription,
      props.showProductDescription,
      props.showVkMultiDescription,
      props.showVkWithoutButtonDescription,
      props.showFbMultiDescription,
      props.tmCarouselDescription
    ]).length > 1 ||
    _.filter([props.showTitle, props.showProductTitle, props.showVkTitle, props.showFbTitle])
      .length > 1

  const showDescription =
    props.showColumnDescription ||
    props.showMultiDescription ||
    props.showProductDescription ||
    props.showVkMultiDescription ||
    props.showVkWithoutButtonDescription ||
    props.showFbMultiDescription ||
    props.showTmCarouselDescription

  const areMultilinesAllowed =
    !props.showColumnDescription && !props.showMultiDescription && !props.showProductDescription
  const descriptionProps = {
    maxLength: descriptionMaxLength,
    value: resultDescription,
    errors: _.uniq([
      ...(props.showColumnDescription ? props.columnDescriptionErrors : []),
      ...(props.showMultiDescription ? props.multiDescriptionErrors : []),
      ...(props.showProductDescription ? props.productDescriptionErrors : []),
      ...(props.showVkMultiDescription ? props.vkMultiDescriptionErrors : []),
      ...(props.showVkWithoutButtonDescription ? props.vkWithoutButtonDescriptionErrors : []),
      ...(props.showFbMultiDescription ? props.fbMultiDescriptionErrors : []),
      ...(props.showTmCarouselDescription ? props.tmCarouselDescriptionErrors : [])
    ]),
    multiline: areMultilinesAllowed,
    onChange: (val) => {
      if (props.showColumnDescription) props.onColumnDescriptionChange(val)
      if (props.showMultiDescription) props.onMultiDescriptionChange(val)
      if (props.showProductDescription) props.onProductDescriptionChange(val)
      if (props.showVkMultiDescription) props.onVkMultiDescriptionChange(val)
      if (props.showVkWithoutButtonDescription) props.onVkWithoutButtonDescriptionChange(val)
      if (props.showFbMultiDescription) props.onFbMultiDescriptionChange(val)
      if (props.showTmCarouselDescription) props.onTmCarouselDescriptionChange(val)
    }
  }

  const isCutViewAvailable =
    uniqTitles.length <= 1 &&
    uniqDescriptions.length <= 1 &&
    (areMultilinesAllowed || resultDescription.indexOf('\n') === -1) &&
    resultTitle.length <= titleMaxLength &&
    resultDescription.length <= descriptionMaxLength

  return {
    showDescription,
    descriptionProps,
    showTitle,
    titleProps,
    isCutViewAvailable,
    isCutViewReasonable
  }
}
